import { useState } from "react"
import { Head, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PhoneInput } from "@/components/ui/phone-input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ArrowRight, Eye, EyeOff, Check, X, GraduationCap, Users, BookOpen, Briefcase } from "lucide-react"
import logo from "@/assets/logo-25segundos-horizontal.png"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import { NavigationHeader } from "@/components/VocationalTest/NavigationHeader"
import { useAuth } from "@/hooks/useAuth"

interface UserPii {
  nome: string | null
  email: string | null
  telefone: string | null
  data_nascimento: string | null
  escola: string | null
  serie: string | null
  sala: string | null
  cidade: string | null
}

interface Props {
  user_pii: UserPii | null
  flash?: {
    notice?: string
    alert?: string
  }
  errors?: Record<string, string[]>
}

// --- Types & constants for multi-step flow ---

type Category = "objetivo" | "seletivo" | "outros" | "externo"
type Role = "Estudante" | "Pais ou Responsáveis" | "Corpo Docente" | "Funcionários"
type FlowStep = "category" | "city" | "role" | "form"

const CATEGORIES: { id: Category; label: string; subtitle: string }[] = [
  { id: "objetivo", label: "Colégio Objetivo", subtitle: "Mogi Guaçu e Mogi Mirim" },
  // { id: "seletivo", label: "Colégio Seletivo", subtitle: "Mogi Guaçu" },
  { id: "outros", label: "Outros Colégios", subtitle: "Informe sua instituição" },
  { id: "externo", label: "Público Externo", subtitle: "Não vinculado a colégio" },
]

const CITIES = [
  { id: "Mogi Guaçu", label: "Mogi Guaçu" },
  { id: "Mogi Mirim", label: "Mogi Mirim" },
]

const ROLES: { id: Role; label: string; icon: typeof GraduationCap }[] = [
  { id: "Estudante", label: "Estudante", icon: GraduationCap },
  { id: "Pais ou Responsáveis", label: "Pais ou Responsáveis", icon: Users },
  { id: "Corpo Docente", label: "Corpo Docente", icon: BookOpen },
  { id: "Funcionários", label: "Funcionários", icon: Briefcase },
]

const GRADES = ["1ª", "2ª", "3ª", "Cursinho"]
const CLASSES = ["A", "B", "C", "D", "E"]

// --- Helper functions ---

function getMaxDateOfBirth(): string {
  const today = new Date()
  const maxDate = new Date(today.getFullYear() - 13, today.getMonth(), today.getDate())
  return maxDate.toISOString().split("T")[0]
}

function getMinDateOfBirth(): string {
  const today = new Date()
  const minDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate())
  return minDate.toISOString().split("T")[0]
}

function validateDateOfBirth(dateStr: string): string | null {
  if (!dateStr) return null

  const birthDate = new Date(dateStr)
  const today = new Date()
  const age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ? age - 1
    : age

  if (actualAge < 13) {
    return "Você precisa ter pelo menos 13 anos para realizar o teste."
  }
  if (actualAge > 100) {
    return "Por favor, insira uma data de nascimento válida."
  }
  return null
}

function validatePassword(password: string): string[] {
  const errors: string[] = []
  if (password.length < 8) errors.push("Mínimo 8 caracteres")
  if (!/[A-Z]/.test(password)) errors.push("Ao menos uma letra maiúscula")
  if (!/[a-z]/.test(password)) errors.push("Ao menos uma letra minúscula")
  if (!/[0-9]/.test(password)) errors.push("Ao menos um número")
  if (!/[^A-Za-z0-9]/.test(password)) errors.push("Ao menos um caractere especial (!@#$%^&*)")
  return errors
}

function composeEscola(category: Category, city: string, role: Role | null, schoolName: string): string {
  if (category === "externo") return "Externo"

  const base = category === "outros"
    ? schoolName
    : category === "objetivo"
      ? `Objetivo ${city}`
      : `Seletivo ${city}`

  return role ? `${base} - ${role}` : base
}

// --- Card styles for category step ---

const categoryCardStyles: Record<Category, string> = {
  objetivo: "from-blue-700 via-blue-800 to-blue-900",
  seletivo: "from-red-700 via-red-800 to-green-800",
  outros: "from-violet-700 via-purple-800 to-indigo-900",
  externo: "from-slate-600 via-slate-700 to-slate-800",
}

const categoryStripeColors: Record<Category, string> = {
  objetivo: "rgba(234,179,8,0.25)",
  seletivo: "rgba(255,255,255,0.15)",
  outros: "rgba(255,255,255,0.1)",
  externo: "rgba(255,255,255,0.08)",
}

export default function AuthFlow({ user_pii, flash, errors }: Props) {
  const { isLoggedIn, user } = useAuth()

  // Auth state
  const [loginEmail, setLoginEmail] = useState("")
  const [loginPassword, setLoginPassword] = useState("")
  const [signupEmail, setSignupEmail] = useState("")
  const [signupPassword, setSignupPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showLoginPassword, setShowLoginPassword] = useState(false)
  const [showSignupPassword, setShowSignupPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordErrors, setPasswordErrors] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [acceptTermsOfUse, setAcceptTermsOfUse] = useState(false)
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false)

  // Multi-step flow state
  const [step, setStep] = useState<FlowStep>("category")
  const [category, setCategory] = useState<Category | null>(null)
  const [city, setCity] = useState("")
  const [role, setRole] = useState<Role | null>(null)
  const [schoolName, setSchoolName] = useState("")

  // Personal info state
  const [nome, setNome] = useState(user?.name || "")
  const [telefone, setTelefone] = useState(user_pii?.telefone || "")
  const [dataNascimento, setDataNascimento] = useState(user_pii?.data_nascimento || "")
  const [serie, setSerie] = useState(user_pii?.serie || "")
  const [sala, setSala] = useState(user_pii?.sala || "")
  const [dateError, setDateError] = useState("")

  // Derived: should we show serie/sala?
  const isStudent = role === "Estudante"
  const needsSchoolNameInput = category === "outros"
  const showSerieAndSala = isStudent

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    router.post("/login",
      { email: loginEmail, password: loginPassword },
      {
        onFinish: () => setIsLoading(false),
        preserveState: true
      }
    )
  }

  const handlePasswordChange = (password: string) => {
    setSignupPassword(password)
    setPasswordErrors(validatePassword(password))
  }

  const getPasswordStrength = () => {
    const errors = passwordErrors.length
    if (errors === 0) return { label: "Forte", color: "bg-green-500", percentage: 100 }
    if (errors <= 2) return { label: "Média", color: "bg-yellow-500", percentage: 60 }
    return { label: "Fraca", color: "bg-red-500", percentage: 30 }
  }

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault()

    if (!acceptTermsOfUse || !acceptPrivacyPolicy) {
      alert("Você precisa aceitar os Termos de Uso e a Política de Privacidade para criar sua conta.")
      return
    }

    if (signupPassword !== confirmPassword) {
      alert("As senhas não coincidem")
      return
    }

    const errors = validatePassword(signupPassword)
    if (errors.length > 0) {
      alert("Por favor, atenda a todos os requisitos de senha:\n" + errors.join("\n"))
      return
    }

    setIsLoading(true)
    router.post("/auth-flow",
      {
        user: {
          email: signupEmail,
          password: signupPassword,
          password_confirmation: confirmPassword
        },
        accept_terms_of_use: acceptTermsOfUse.toString(),
        accept_privacy_policy: acceptPrivacyPolicy.toString(),
      },
      {
        onFinish: () => setIsLoading(false),
        preserveState: true
      }
    )
  }

  // --- Step navigation handlers ---

  const handleSelectCategory = (cat: Category) => {
    setCategory(cat)
    if (cat === "objetivo") {
      setStep("city")
    } else if (cat === "seletivo") {
      setCity("Mogi Guaçu")
      setStep("role")
    } else if (cat === "outros") {
      setStep("role")
    } else {
      // externo → straight to form
      setStep("form")
    }
  }

  const handleSelectCity = (selectedCity: string) => {
    setCity(selectedCity)
    setStep("role")
  }

  const handleSelectRole = (selectedRole: Role) => {
    setRole(selectedRole)
    setStep("form")
  }

  const handleStepBack = () => {
    if (step === "form") {
      if (category === "externo") {
        setStep("category")
      } else {
        setStep("role")
      }
    } else if (step === "role") {
      if (category === "objetivo") {
        setStep("city")
      } else {
        setStep("category")
      }
    } else if (step === "city") {
      setStep("category")
    }
  }

  // --- Personal info handlers ---

  const handleDateChange = (value: string) => {
    setDataNascimento(value)
    const error = validateDateOfBirth(value)
    setDateError(error || "")
  }

  const handlePersonalInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (dataNascimento) {
      const error = validateDateOfBirth(dataNascimento)
      if (error) {
        setDateError(error)
        return
      }
    }

    const escola = composeEscola(category!, city, role, schoolName)

    setIsLoading(true)
    router.post("/auth-flow/personal-info", {
      nome,
      telefone,
      data_nascimento: dataNascimento,
      escola,
      serie: showSerieAndSala ? serie : "",
      sala: showSerieAndSala ? sala : "",
    }, {
      onFinish: () => setIsLoading(false)
    })
  }

  const isPersonalInfoValid =
    nome &&
    telefone &&
    dataNascimento &&
    !dateError &&
    (needsSchoolNameInput ? schoolName.trim() : true) &&
    (showSerieAndSala ? serie && sala : true)

  // =================================================================
  // LOGGED IN → multi-step flow
  // =================================================================
  if (isLoggedIn) {
    // --- Step 1: Category ---
    if (step === "category") {
      return (
        <>
          <Head title="Escolha sua categoria - Orientação Profissional" />
          <PageLayout>
            <div className="w-full max-w-lg relative">
              <NavigationHeader onBack={() => router.visit("/about-test")} />
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-title-blue">Como você se identifica?</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Selecione a opção que melhor descreve sua situação
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleSelectCategory(cat.id)}
                      className={`relative overflow-hidden rounded-xl shadow-card hover-scale cursor-pointer text-left h-36${cat.id === "objetivo" ? " col-span-2" : ""}`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${categoryCardStyles[cat.id]}`} />
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `repeating-linear-gradient(
                            -45deg,
                            transparent,
                            transparent 8px,
                            ${categoryStripeColors[cat.id]} 8px,
                            ${categoryStripeColors[cat.id]} 10px
                          )`,
                          mask: "linear-gradient(135deg, transparent 40%, black 100%)",
                          WebkitMask: "linear-gradient(135deg, transparent 40%, black 100%)",
                        }}
                      />
                      <div className="relative z-10 p-4 h-full flex flex-col justify-between">
                        <span className="text-white font-bold text-sm leading-tight">{cat.label}</span>
                        <span className="text-white/70 text-xs">{cat.subtitle}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </PageLayout>
        </>
      )
    }

    // --- Step 2: City (Objetivo only) ---
    if (step === "city") {
      return (
        <>
          <Head title="Selecione a cidade - Orientação Profissional" />
          <PageLayout>
            <div className="w-full max-w-md relative">
              <NavigationHeader onBack={handleStepBack} />
              <Card className="w-full p-8 shadow-elegant">
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-title-blue">Colégio Objetivo</h2>
                    <p className="text-sm text-muted-foreground mt-2">
                      Selecione a unidade
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {CITIES.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleSelectCity(c.id)}
                        className="p-6 rounded-xl border-2 border-blue-200 bg-blue-50 hover:border-blue-400 hover:bg-blue-100 transition-all cursor-pointer text-center hover-scale"
                      >
                        <span className="text-lg font-semibold text-blue-900">{c.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </PageLayout>
        </>
      )
    }

    // --- Step 3: Role ---
    if (step === "role") {
      const categoryLabel = category === "objetivo"
        ? `Objetivo ${city}`
        : category === "seletivo"
          ? "Seletivo Mogi Guaçu"
          : "Outros Colégios"

      return (
        <>
          <Head title="Qual seu perfil? - Orientação Profissional" />
          <PageLayout>
            <div className="w-full max-w-md relative">
              <NavigationHeader onBack={handleStepBack} />
              <Card className="w-full p-8 shadow-elegant">
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-title-blue">Qual seu perfil?</h2>
                    <p className="text-sm text-muted-foreground mt-2">
                      {categoryLabel}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {ROLES.map((r) => {
                      const Icon = r.icon
                      return (
                        <button
                          key={r.id}
                          onClick={() => handleSelectRole(r.id)}
                          className="p-5 rounded-xl border-2 border-border hover:border-primary/50 hover:bg-accent/20 transition-all cursor-pointer text-center hover-scale flex flex-col items-center gap-3"
                        >
                          <Icon className="h-8 w-8 text-primary/70" />
                          <span className="text-sm font-semibold text-foreground">{r.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </Card>
            </div>
          </PageLayout>
        </>
      )
    }

    // --- Step 4: Personal info form ---
    return (
      <>
        <Head title="Dados Pessoais - Orientação Profissional" />
        <PageLayout>
          <div className="w-full max-w-md relative">
            <NavigationHeader onBack={handleStepBack} />
            <Card className="w-full p-8 shadow-elegant">
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-title-blue">Dados Pessoais</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Preencha seus dados para começar o teste
                  </p>
                </div>

                {flash?.alert && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                    {flash.alert}
                  </div>
                )}

                <form onSubmit={handlePersonalInfoSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome</Label>
                    <Input
                      id="nome"
                      type="text"
                      placeholder="Seu nome completo"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telefone">Telefone</Label>
                    <PhoneInput
                      id="telefone"
                      value={telefone}
                      onChange={(value) => setTelefone(value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dataNascimento">Data de Nascimento</Label>
                    <Input
                      id="dataNascimento"
                      type="date"
                      min={getMinDateOfBirth()}
                      max={getMaxDateOfBirth()}
                      value={dataNascimento}
                      onChange={(e) => handleDateChange(e.target.value)}
                      required
                      className={dateError ? "border-destructive" : ""}
                    />
                    {dateError && <p className="text-sm text-destructive">{dateError}</p>}
                  </div>

                  {needsSchoolNameInput && (
                    <div className="space-y-2">
                      <Label htmlFor="schoolName">Nome do colégio</Label>
                      <Input
                        id="schoolName"
                        type="text"
                        placeholder="Digite o nome do colégio"
                        value={schoolName}
                        onChange={(e) => setSchoolName(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {showSerieAndSala && (
                    <div className="flex gap-3">
                      <div className="space-y-2 flex-1">
                        <Label htmlFor="serie">Série</Label>
                        <select
                          id="serie"
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={serie}
                          onChange={(e) => setSerie(e.target.value)}
                          required
                        >
                          <option value="" disabled>
                            Selecione
                          </option>
                          {GRADES.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2 flex-1">
                        <Label htmlFor="sala">Sala</Label>
                        <select
                          id="sala"
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={sala}
                          onChange={(e) => setSala(e.target.value)}
                          required
                        >
                          <option value="" disabled>
                            Selecione
                          </option>
                          {CLASSES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-gradient-primary hover:opacity-90 transition-opacity"
                    size="lg"
                    disabled={!isPersonalInfoValid || isLoading}
                  >
                    {isLoading ? "Salvando..." : "Continuar"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </div>
            </Card>
          </div>
        </PageLayout>
      </>
    )
  }

  // =================================================================
  // NOT LOGGED IN → login/signup tabs
  // =================================================================
  return (
    <>
      <Head title="Entrar - Orientação Profissional" />
      <PageLayout>
        <div className="w-full max-w-md relative">
          <NavigationHeader onBack={() => router.visit("/about-test")} />
          <Card className="w-full p-8 shadow-elegant">
            <div className="space-y-6">
              <div className="flex justify-center">
                <img src={logo} alt="25Segundos" className="h-16" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold text-title-blue">Orientação Profissional</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Crie sua conta ou faça login para continuar
                </p>
              </div>

              {flash?.notice && (
                <div className="p-4 bg-green-100 text-green-800 rounded-lg text-sm">
                  {flash.notice}
                </div>
              )}

              {flash?.alert && (
                <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                  {flash.alert}
                </div>
              )}

              {errors?.base && (
                <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                  {errors.base[0]}
                </div>
              )}

              <Tabs defaultValue="cadastro" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="cadastro">Cadastro</TabsTrigger>
                  <TabsTrigger value="login">Login</TabsTrigger>
                </TabsList>

                <TabsContent value="cadastro">
                  <form onSubmit={handleSignup} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="seu@email.com"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        required
                      />
                      {errors?.email && (
                        <p className="text-sm text-red-500">{errors.email[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Senha</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showSignupPassword ? "text" : "password"}
                          placeholder="********"
                          value={signupPassword}
                          onChange={(e) => handlePasswordChange(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword(!showSignupPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors?.password && (
                        <p className="text-sm text-red-500">{errors.password[0]}</p>
                      )}

                      {signupPassword && (
                        <>
                          <div className="space-y-2 mt-2">
                            <div className="flex items-center justify-between text-xs">
                              <span>Força da senha:</span>
                              <span className={`font-semibold ${
                                passwordErrors.length === 0 ? "text-green-600" :
                                passwordErrors.length <= 2 ? "text-yellow-600" :
                                "text-red-600"
                              }`}>
                                {getPasswordStrength().label}
                              </span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-300 ${getPasswordStrength().color}`}
                                style={{ width: `${getPasswordStrength().percentage}%` }}
                              />
                            </div>
                          </div>

                          <div className="space-y-1 mt-3">
                            <p className="text-xs font-medium text-muted-foreground">Requisitos:</p>
                            {[
                              { key: "length", label: "Mínimo 8 caracteres", test: signupPassword.length >= 8 },
                              { key: "uppercase", label: "Letra maiúscula (A-Z)", test: /[A-Z]/.test(signupPassword) },
                              { key: "lowercase", label: "Letra minúscula (a-z)", test: /[a-z]/.test(signupPassword) },
                              { key: "number", label: "Número (0-9)", test: /[0-9]/.test(signupPassword) },
                              { key: "special", label: "Caractere especial (!@#$%)", test: /[^A-Za-z0-9]/.test(signupPassword) },
                            ].map((req) => (
                              <div key={req.key} className="flex items-center gap-2 text-xs">
                                {req.test ? (
                                  <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
                                ) : (
                                  <X className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                )}
                                <span className={req.test ? "text-green-600" : "text-muted-foreground"}>
                                  {req.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirmar Senha</Label>
                      <div className="relative">
                        <Input
                          id="confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="********"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          className={`pr-10 ${confirmPassword && signupPassword !== confirmPassword ? "border-destructive" : ""}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {confirmPassword && signupPassword !== confirmPassword && (
                        <p className="text-xs text-destructive">As senhas não coincidem</p>
                      )}
                      {errors?.password_confirmation && (
                        <p className="text-sm text-red-500">{errors.password_confirmation[0]}</p>
                      )}
                    </div>

                    {/* Termos e Privacidade */}
                    <div className="space-y-3 pt-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptTermsOfUse}
                          onChange={(e) => setAcceptTermsOfUse(e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                        />
                        <span className="text-sm text-muted-foreground leading-tight">
                          Li e aceito os{" "}
                          <a
                            href="/termos-de-uso"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Termos de Uso
                          </a>
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptPrivacyPolicy}
                          onChange={(e) => setAcceptPrivacyPolicy(e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                        />
                        <span className="text-sm text-muted-foreground leading-tight">
                          Li e aceito a{" "}
                          <a
                            href="/politica-de-privacidade"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Política de Privacidade
                          </a>
                        </span>
                      </label>
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading || !acceptTermsOfUse || !acceptPrivacyPolicy}>
                      {isLoading ? "Criando conta..." : "Criar conta"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="seu@email.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="login-password">Senha</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showLoginPassword ? "text" : "password"}
                          placeholder="********"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? "Entrando..." : "Entrar"}
                    </Button>

                    <button
                      type="button"
                      className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                      onClick={() => router.visit("/esqueci-senha")}
                    >
                      Esqueci minha senha
                    </button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          </Card>
        </div>
      </PageLayout>
    </>
  )
}